import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type {
  Customer,
  CustomerCreate,
  CustomerList,
  CustomerStats,
  LocationList,
  MaintenanceWindowParseResult,
  CustomerUpdate,
} from '@/types/api';

// ---------------------------------------------------------------------------
// S8-06: api.gen.ts ist regeneriert - die Customer-Schemas führen
// legal_name/contact_phone/notification_emails/maintenance_frequency jetzt
// selbst. Die Aliase bleiben als stabile Namen für die Konsumenten bestehen.
// ---------------------------------------------------------------------------

export type CustomerMaintenanceFrequency = NonNullable<Customer['maintenance_frequency']>;

export type CustomerDetail = Customer;
/**
 * `force` ist im OpenAPI-Schema required (default false), am Wire aber
 * optional. Ohne diesen Alias müsste jeder Anlege-Call `force: false`
 * mitschicken — das Formular kennt den Schalter nicht und soll ihn auch
 * nicht kennen: die Dubletten-Bremse sitzt vor dem Formular.
 */
export type CustomerCreateInput = Omit<CustomerCreate, 'force'> & {
  force?: boolean;
};
export type CustomerUpdateInput = CustomerUpdate;

export interface CustomersFilter {
  search?: string;
  hasActiveContract?: boolean;
  cursor?: string;
  limit?: number;
  /**
   * Komma-Liste zusätzlicher Felder. `stats` aggregiert
   * locations_count/systems_count/last_maintenance_at/active_contract_status.
   */
  expand?: 'stats';
  /**
   * true = nur archivierte, false = nur aktive. Ohne Angabe lässt das
   * Backend nur Aktive durch — nach dem Vorfall 04.09.2026 (Deep-Link auf
   * die gelöschte Winkler-Dublette) wollen wir in der Liste aber
   * ausdrücklich `false` senden, damit nichts Archiviertes durchrutscht.
   */
  archived?: boolean;
  /** Aktive und archivierte zusammen (nicht für die Listenansicht). */
  includeArchived?: boolean;
}

/**
 * Hooks für das Customer-Subdomain.
 *
 * Typisierung: Aliase aus `@/types/api` (= dünne Schicht über dem
 * generierten `api.gen.ts`). So bleibt jeder Drift sofort als TS-Fehler
 * sichtbar, sobald `pnpm gen:types` neu läuft.
 */

export function useCustomers(filter: CustomersFilter = {}): ReturnType<
  typeof useQuery<CustomerList>
> {
  const params: Record<string, string | number | boolean | undefined> = {
    'filter[search]': filter.search,
    'filter[has_active_contract]': filter.hasActiveContract,
    'filter[archived]': filter.archived,
    include_archived: filter.includeArchived,
    cursor: filter.cursor,
    limit: filter.limit ?? 50,
    expand: filter.expand,
  };

  return useQuery<CustomerList>({
    queryKey: queryKeys.customers.list(filter as Record<string, unknown>),
    queryFn: () => api.get<CustomerList>('/customers', params),
  });
}

/**
 * Anzahl archivierter Kunden für den Umschalter in der Liste.
 * Nutzt bewusst die bestehende Liste mit limit=1: meta.total_estimate ist
 * filterbewusst, eine eigene Zählroute wäre doppelte Wahrheit.
 */
export function useArchivedCustomerCount(): number | null {
  const query = useCustomers({ archived: true, limit: 1 });
  return query.data?.meta?.total_estimate ?? null;
}

export function useCustomer(
  id: string | undefined
): ReturnType<typeof useQuery<CustomerDetail>> {
  return useQuery<CustomerDetail>({
    queryKey: queryKeys.customers.detail(id ?? ''),
    queryFn: () => api.get<CustomerDetail>(`/customers/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useCustomerLocations(
  customerId: string | undefined
): ReturnType<typeof useQuery<LocationList>> {
  return useQuery<LocationList>({
    queryKey: queryKeys.customers.locations(customerId ?? ''),
    queryFn: () => api.get<LocationList>(`/customers/${customerId ?? ''}/locations`),
    enabled: Boolean(customerId),
  });
}

export function useCustomerStats(
  customerId: string | undefined
): ReturnType<typeof useQuery<CustomerStats>> {
  return useQuery<CustomerStats>({
    queryKey: queryKeys.customers.stats(customerId ?? ''),
    queryFn: () => api.get<CustomerStats>(`/customers/${customerId ?? ''}/stats`),
    enabled: Boolean(customerId),
  });
}

export interface UpdateCustomerInput {
  customerId: string;
  body: CustomerUpdateInput;
}

export function useUpdateCustomer(): ReturnType<
  typeof useMutation<CustomerDetail, Error, UpdateCustomerInput>
> {
  const qc = useQueryClient();
  return useMutation<CustomerDetail, Error, UpdateCustomerInput>({
    mutationFn: ({ customerId, body }) =>
      api.patch<CustomerDetail, CustomerUpdateInput>(`/customers/${customerId}`, body),
    onSuccess: (customer) => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.detail(customer.id) });
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/**
 * Kunde anlegen (Spec S8-06 §5). Invalidiert den kompletten customers-Baum
 * (Liste, Stats, Locations liegen alle unter dem 'customers'-Präfix).
 */
export function useCreateCustomer(): ReturnType<
  typeof useMutation<CustomerDetail, Error, CustomerCreateInput>
> {
  const qc = useQueryClient();
  return useMutation<CustomerDetail, Error, CustomerCreateInput>({
    mutationFn: (body) => api.post<CustomerDetail, CustomerCreateInput>('/customers', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

export interface DeleteCustomerInput {
  customerId: string;
  /** true = endgültig löschen (nur bei bereits archivierten Kunden). */
  hard?: boolean;
}

/**
 * Kunde löschen.
 *
 * Ohne `hard`: archivieren (Verträge werden abgehängt, die Invoice-Ninja-Id
 * wird freigegeben). Der 04.09.2026-Vorfall hat gezeigt, dass "Loeschen" vom
 * Betreiber als Archiv verstanden wird — und ein Archiv muss wiederherstellbar
 * sein, nicht unsichtbar-aber-weiter-da.
 *
 * `hard: true` löscht endgültig (nur wenn der Kunde schon archiviert ist
 * und keine Daten mehr daran hängen).
 */
export function useDeleteCustomer(): ReturnType<
  typeof useMutation<void, Error, DeleteCustomerInput>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteCustomerInput>({
    mutationFn: ({ customerId, hard }) =>
      api.delete(`/customers/${customerId}${hard ? '?hard=true' : ''}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/** Archivierten Kunden wiederherstellen (POST /customers/{id}/restore). */
export function useRestoreCustomer(): ReturnType<
  typeof useMutation<CustomerDetail, Error, string>
> {
  const qc = useQueryClient();
  return useMutation<CustomerDetail, Error, string>({
    mutationFn: (customerId) =>
      api.post<CustomerDetail>(`/customers/${customerId}/restore`),
    onSuccess: (customer) => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
      void qc.invalidateQueries({ queryKey: queryKeys.customers.detail(customer.id) });
    },
  });
}

/**
 * Heuristik-Parser für Wartungsfenster aus Freitext-Notes (WT-205).
 * Persistiert nichts - User entscheidet im UI über accept/discard.
 */
export function useParseWindowFromNotes(): ReturnType<
  typeof useMutation<MaintenanceWindowParseResult, Error, string>
> {
  return useMutation<MaintenanceWindowParseResult, Error, string>({
    mutationFn: (customerId) =>
      api.post<MaintenanceWindowParseResult>(
        `/customers/${customerId}/parse-window-from-notes`
      ),
  });
}
